/**
 * AOS-PEO-03 — Identity Management
 *
 * HR-only UI for establishing and revoking auth-to-worker identity links.
 * Uses the external_identity table via PEO-03 API.
 *
 * Security enforced server-side:
 *  - One email → one active worker
 *  - One worker → one active email
 *  - Cross-tenant links blocked
 *  - Audit provenance stored (mapped_by / revoked_by)
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Link2, LinkIcon, Users, Search, ShieldCheck, AlertCircle,
  CheckCircle2, XCircle, RefreshCw, Unlink, Mail, Eye,
  Plus, Trash2, Clock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
}

// ── Link Modal ────────────────────────────────────────────────────────────────

function LinkModal({ worker, onConfirm, onCancel, loading, error }) {
  const [email, setEmail]   = useState("");
  const [notes, setNotes]   = useState("");
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-md mx-4 p-6"
           data-testid="link-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Link Identity</h3>
            <p className="text-xs text-muted-foreground">{worker?.display_name} ({worker?.worker_number})</p>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm flex items-center gap-2"
               data-testid="link-modal-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground/70 block mb-1">
              Auth Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                placeholder="employee@institution.edu"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="link-email-input"
                autoFocus
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Must match the user's login email exactly. Case-insensitive.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-foreground/70 block mb-1">Notes (optional)</label>
            <input
              type="text"
              placeholder="e.g. Verified via student ID — 2026 onboarding"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-border bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="link-notes-input"
            />
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-4 text-xs text-amber-800">
          <strong>Caution:</strong> This link grants the user at <em>{email || "the entered email"}</em> access to{" "}
          {worker?.display_name}'s employee records. Verify the identity carefully before confirming.
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="link-modal-cancel"
          >Cancel</button>
          <button
            onClick={() => onConfirm(email.trim(), notes.trim())}
            disabled={!isValid || loading}
            className="px-4 py-2 rounded-xl text-sm bg-primary text-primary-foreground font-medium hover:opacity-90 disabled:opacity-50 transition"
            data-testid="link-modal-confirm"
          >
            {loading ? "Linking…" : "Create Identity Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Revoke Modal ──────────────────────────────────────────────────────────────

function RevokeModal({ worker, onConfirm, onCancel, loading, error }) {
  const [reason, setReason] = useState("");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-md mx-4 p-6"
           data-testid="revoke-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <Unlink className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Revoke Identity Link</h3>
            <p className="text-xs text-muted-foreground">{worker?.display_name} ({worker?.worker_number})</p>
          </div>
        </div>

        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm flex items-center gap-2"
               data-testid="revoke-modal-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <p className="text-sm text-muted-foreground mb-3">
          Revoking this link will immediately remove{" "}
          <strong>{worker?.linked_email}</strong>'s access to{" "}
          {worker?.display_name}'s employee self-service records.
        </p>

        <div>
          <label className="text-xs font-medium text-foreground/70 block mb-1">
            Reason for revocation <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="e.g. Employee resigned / identity change request"
            value={reason}
            onChange={e => setReason(e.target.value)}
            data-testid="revoke-reason-input"
            autoFocus
          />
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="revoke-modal-cancel"
          >Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || loading}
            className="px-4 py-2 rounded-xl text-sm bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition"
            data-testid="revoke-modal-confirm"
          >
            {loading ? "Revoking…" : "Revoke Link"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function IdentityManagement() {
  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [workers, setWorkers]       = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  // Modal state
  const [linkTarget, setLinkTarget]     = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const loadWorkers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      const res = await axios.get(`${API}/api/v1/peo03/workers-for-linking`, { headers, params });
      setWorkers(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "object" ? JSON.stringify(d) : (d || err.message));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(loadWorkers, 300);
    return () => clearTimeout(t);
  }, [loadWorkers]);

  const handleLinkConfirm = async (email, notes) => {
    if (!linkTarget) return;
    setActionLoading(true);
    setActionError("");
    setActionSuccess("");
    try {
      await axios.post(
        `${API}/api/v1/peo03/identity-links`,
        { worker_id: linkTarget.worker_id, auth_email: email, notes },
        { headers },
      );
      setActionSuccess(`Identity link created for ${linkTarget.display_name}.`);
      setLinkTarget(null);
      loadWorkers();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "object" ? JSON.stringify(d) : (d || err.message);
      setActionError(msg);
      // Keep modal open so user can fix the issue
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeConfirm = async (reason) => {
    if (!revokeTarget) return;
    setActionLoading(true);
    setActionError("");
    setActionSuccess("");
    try {
      await axios.delete(
        `${API}/api/v1/peo03/identity-links/${revokeTarget.mapping_id}`,
        { headers, data: { reason } },
      );
      setActionSuccess(`Identity link revoked for ${revokeTarget.display_name}.`);
      setRevokeTarget(null);
      loadWorkers();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "object" ? JSON.stringify(d) : (d || err.message);
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const linked   = workers.filter(w => w.is_linked).length;
  const unlinked = workers.filter(w => !w.is_linked).length;

  return (
    <div className="min-h-screen bg-background" data-testid="identity-management-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="identity-management-title">
              Identity Management
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Govern auth-to-worker linkages · HR Authorised Only
            </p>
          </div>
          <button
            onClick={loadWorkers}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="refresh-identity-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Feedback banners ─────────────────────────────────────────────── */}
        {actionSuccess && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-sm flex items-center gap-2"
               data-testid="identity-success-banner">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {actionSuccess}
          </div>
        )}
        {actionError && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm flex items-center gap-2"
               data-testid="identity-error-banner">
            <XCircle className="h-4 w-4 shrink-0" />
            {actionError}
          </div>
        )}

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-4" data-testid="identity-stats">
          <div className="rounded-xl border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-foreground" data-testid="stat-total-workers">{workers.length}</div>
            <div className="text-xs text-muted-foreground mt-0.5">Active Workers</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
            <div className="text-2xl font-bold text-emerald-700" data-testid="stat-linked-workers">{linked}</div>
            <div className="text-xs text-emerald-600/70 mt-0.5">Linked</div>
          </div>
          <div className={`rounded-xl border p-4 text-center ${unlinked > 0 ? "border-amber-200 bg-amber-50" : "border-border bg-card"}`}>
            <div className={`text-2xl font-bold ${unlinked > 0 ? "text-amber-700" : "text-foreground"}`}
                 data-testid="stat-unlinked-workers">{unlinked}</div>
            <div className={`text-xs mt-0.5 ${unlinked > 0 ? "text-amber-600/70" : "text-muted-foreground"}`}>Unlinked</div>
          </div>
        </div>

        {/* ── Security notice ──────────────────────────────────────────────── */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex gap-2 items-start"
             data-testid="identity-security-notice">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Identity links are <strong>auditable</strong> and institution-scoped.
            Each email maps to exactly one worker. Email address alone does not authorize a link —
            HR staff must verify identity before creating a link.
          </span>
        </div>

        {/* ── Search ───────────────────────────────────────────────────────── */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name or worker number…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="identity-search-input"
          />
        </div>

        {/* ── Error ──────────────────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-center gap-2"
               data-testid="identity-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* ── Worker list ──────────────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="identity-loading">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Loading workers…
          </div>
        ) : workers.length === 0 ? (
          <div className="rounded-xl border border-border bg-card text-center py-16 text-muted-foreground"
               data-testid="identity-empty">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No workers found</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="identity-workers-table">
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Worker</th>
                    <th className="text-left px-4 py-3">Number</th>
                    <th className="text-left px-4 py-3">Link Status</th>
                    <th className="text-left px-4 py-3">Linked Email</th>
                    <th className="text-right px-4 py-3 pr-6">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {workers.map(w => (
                    <tr key={w.worker_id}
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`worker-row-${w.worker_id}`}>
                      <td className="px-4 py-3 font-medium text-foreground">{w.display_name}</td>
                      <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{w.worker_number}</td>
                      <td className="px-4 py-3">
                        {w.is_linked ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"
                                data-testid={`link-status-linked-${w.worker_id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Linked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200"
                                data-testid={`link-status-unlinked-${w.worker_id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Not Linked
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {w.linked_email ? (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" />
                            {w.linked_email}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 pr-6 text-right">
                        {w.is_linked ? (
                          <button
                            onClick={() => { setActionError(""); setRevokeTarget(w); }}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 transition ml-auto"
                            data-testid={`revoke-identity-btn-${w.worker_id}`}
                          >
                            <Unlink className="h-3.5 w-3.5" />
                            Revoke
                          </button>
                        ) : (
                          <button
                            onClick={() => { setActionError(""); setLinkTarget(w); }}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-primary bg-primary/10 border border-primary/20 hover:bg-primary/20 transition ml-auto"
                            data-testid={`link-identity-btn-${w.worker_id}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Link
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-border">
              {workers.map(w => (
                <div key={w.worker_id} className="p-4 space-y-3" data-testid={`worker-card-${w.worker_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-foreground">{w.display_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{w.worker_number}</div>
                    </div>
                    {w.is_linked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                        Linked
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                        Not Linked
                      </span>
                    )}
                  </div>
                  {w.linked_email && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" /> {w.linked_email}
                    </div>
                  )}
                  {w.is_linked ? (
                    <button
                      onClick={() => { setActionError(""); setRevokeTarget(w); }}
                      className="w-full flex items-center justify-center gap-1 py-2 rounded-xl text-sm text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 transition"
                      data-testid={`mobile-revoke-btn-${w.worker_id}`}
                    >
                      <Unlink className="h-4 w-4" /> Revoke Link
                    </button>
                  ) : (
                    <button
                      onClick={() => { setActionError(""); setLinkTarget(w); }}
                      className="w-full flex items-center justify-center gap-1 py-2 rounded-xl text-sm font-medium bg-primary text-primary-foreground hover:opacity-90 transition"
                      data-testid={`mobile-link-btn-${w.worker_id}`}
                    >
                      <Plus className="h-4 w-4" /> Create Link
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {linkTarget && (
        <LinkModal
          worker={linkTarget}
          onConfirm={handleLinkConfirm}
          onCancel={() => { setLinkTarget(null); setActionError(""); }}
          loading={actionLoading}
          error={actionError}
        />
      )}
      {revokeTarget && (
        <RevokeModal
          worker={revokeTarget}
          onConfirm={handleRevokeConfirm}
          onCancel={() => { setRevokeTarget(null); setActionError(""); }}
          loading={actionLoading}
          error={actionError}
        />
      )}
    </div>
  );
}
